import { setFullScreenCelebrationFilter } from 'components/ui-helper';
import * as particles from 'pixi-particles';
import { Sprite } from 'pixi.js';
import Components from 'slotworks/components/components';
import Layers from 'slotworks/graphics/layers/layers';
import { AbstractSceneTransition } from 'slotworks/graphics/layers/scene-transitions/abstract-scene-transition';
import { CenterPivot, Ungroup } from 'slotworks/graphics/pixi/group';
import betModel from 'slotworks/model/bets/bet-model';
import GameState from 'slotworks/model/game-state';
import FreespinRecord from 'slotworks/model/gameplay/records/freespin-record';
import Contract from 'slotworks/utils/contract';
import Point from 'slotworks/utils/geom/point';
import { sequence } from 'slotworks/utils/race-utils';
import Timer from 'slotworks/utils/timer';
import * as TweenJS from 'slotworks/utils/tween';

export default class RevolverWinTransitions extends AbstractSceneTransition {
    public BOUNCE_TIME: number = 700;
    public HOVER_SCALE: number = 1.15;
    public HOVER_TIME: number = 1000;
    public BOTTOM_LINE_DELAY: number = 100;
    public EXPLODE_DURATION: number = 250;
    public TITLE_ZOOM_TWEEN = 1.2;

    protected static currentTiers: number;

    protected static coins: particles.Emitter;

    protected static glowStack: PIXI.Sprite[] = [];
    protected static glowContainer: PIXI.Container;
    protected static glowTweens: TweenJS.Tween[];

    protected static winValue: PIXI.extras.BitmapText;
    protected static winText: PIXI.Sprite;

    protected static winTextHover: TweenJS.Tween;
    protected static winValueHover: TweenJS.Tween;

    protected static background: PIXI.Sprite;

    protected timers: number[] = [];

    protected inResolve: Function;

    protected resolveIn() {
        if (this.inResolve) {
            this.inResolve();
        }

        this.inResolve = null;
    }

    protected zoomInStacks(time: number = 350, glowScale: number = 1, delay: number = 0) {
        RevolverWinTransitions.glowContainer = new PIXI.Container();
        Layers.CelebrationBackground.container.addChild(RevolverWinTransitions.glowContainer);

        RevolverWinTransitions.glowContainer.position.set(
            RevolverWinTransitions.glowStack[0].x + RevolverWinTransitions.glowStack[0].width / 2,
            RevolverWinTransitions.glowStack[0].y + RevolverWinTransitions.glowStack[0].height / 2
        );
        for (let i = 0; i < RevolverWinTransitions.glowStack.length; i++) {
            RevolverWinTransitions.glowStack[i].anchor.set(0.5, 0.5);
            RevolverWinTransitions.glowStack[i].position.set(0, 0);
            RevolverWinTransitions.glowContainer.addChild(RevolverWinTransitions.glowStack[i]);
        }

        this.startGlowStack();
        RevolverWinTransitions.glowContainer.scale.set(0, 0);
        return new Contract(resolve => {
            new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                .to({ x: 1.6, y: 1.6 }, time)
                .delay(delay)
                .chain(
                    new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale).to({ x: glowScale, y: glowScale }, time).onComplete(() => {
                        resolve(null);
                    })
                )
                .start();
        });
    }

    protected zoomOutStacks() {
        return new Contract(resolve => {
            new TweenJS.Tween(RevolverWinTransitions.glowContainer)
                .to({ alpha: 0 }, 200)
                .delay(50)
                .onComplete(() => {
                    this.stopGlowStack();
                    RevolverWinTransitions.glowStack.forEach(element => {
                        element.x = RevolverWinTransitions.glowContainer.x;
                        element.y = RevolverWinTransitions.glowContainer.y;
                        Layers.CelebrationBackground.container.addChild(element);
                        element.visible = false;
                    });
                    resolve(null);
                })
                .start();
        });
    }

    protected scaleStacks(scale: number, time: number) {
        return new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale).to({ x: scale, scale: 0 }, time).start();
    }

    protected stopGlowStack() {
        RevolverWinTransitions.glowTweens.forEach(tween => {
            tween.stop();
        });
        RevolverWinTransitions.glowTweens = [];
    }

    public zoom(target: PIXI.Container, scaleFrom: number, scaleTo: number, duration: number, easing: (k: number) => number, pivot: Point = null): Contract<void> {
        return new Contract<void>(resolve => {
            const group = CenterPivot(target, null, pivot);
            group.scale.set(scaleFrom, scaleFrom);

            const tween = new TweenJS.Tween(target.scale).to({ x: scaleTo, y: scaleTo }, duration).easing(easing);
            tween.onComplete(() => {
                tween.onComplete(null);
                Ungroup(group);
                target.scale.set(scaleTo, scaleTo);
                resolve(null);
            });
            tween.start();
        });
    }

    protected zoomTextIn(middleScale: number, endScale: number, delay: number = 0): Contract<void> {
        return new Contract<void>(resolve => {
            const target = RevolverWinTransitions.winValue;
            target.visible = true;
            ((target as any) as PIXI.Sprite).anchor.set(0.5, 0.1);
            target.y -= target.height * 0.5;
            TweenJS.removeAll(target.scale);
            target.scale.set(0, 0);
            new TweenJS.Tween(target.scale)
                .to({ x: middleScale, y: middleScale }, 100)
                .delay(delay)
                .onComplete(() => {
                    TweenJS.removeAll(target.scale);
                    new TweenJS.Tween(target.scale)
                        .to({ x: endScale, y: endScale }, 100)
                        .onComplete(() => {
                            TweenJS.removeAll(target.scale);
                            this.hover(target, 1.02, 700, false); // 50 wait
                            resolve(null);
                        })
                        .start();
                })
                .start();
        });
    }

    protected titleIn(target: PIXI.Sprite, pivot: Point) {
        target.visible = true;
        return sequence([
            () => this.zoom(target, 0, 1.2, 350, TweenJS.Easing.Sinusoidal.Out, pivot),
            () => this.zoom(target, 1.2, 1, 175, TweenJS.Easing.Sinusoidal.Out, pivot),
            () =>
                Contract.wrap(() => {
                    this.hover(target, 1.15, 350, false);
                })
        ]);
    }

    protected titleOut(target: PIXI.Container, pivot: Point, time: number) {
        return this.zoom(target, 1, 0, time, TweenJS.Easing.Sinusoidal.Out, pivot);
    }

    protected introduce(target: PIXI.Container, duration: number, pivot: Point, startSize: number = 0) {
        return sequence([
            () => this.zoom(target, startSize, 1.2, duration * 0.75, TweenJS.Easing.Sinusoidal.Out, pivot),
            () => this.zoom(target, 1.2, 1, duration * 0.25, TweenJS.Easing.Sinusoidal.Out, pivot)
        ]);
    }

    protected startGlowStack() {
        RevolverWinTransitions.glowTweens = [];

        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(RevolverWinTransitions.glowStack[0])
                .to({ alpha: 0.9 }, 250)
                .repeat(Infinity)
                .delay(100)
                .yoyo(true)
                .start()
        );

        const glow = RevolverWinTransitions.glowStack[2];
        const rotation = Math.PI / 64;
        glow.rotation = -rotation;
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(glow)
                .to({ rotation }, 2500)
                .repeat(Infinity)
                .yoyo(true)
                .start()
        );
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(glow)
                .to({ alpha: 0.8 }, 200)
                .repeat(Infinity)
                .delay(100)
                .yoyo(true)
                .start()
        );
    }

    protected hoverTitle(title: PIXI.Sprite, repeats: number = 1) {
        TweenJS.removeAll(title.scale);
        new TweenJS.Tween(title.scale)
            .to({ x: this.TITLE_ZOOM_TWEEN, y: this.TITLE_ZOOM_TWEEN }, 1000)
            .yoyo(true)
            .repeat(repeats)
            .onComplete(() => {
                this.zoom(title, title.scale.x, 0, 300, TweenJS.Easing.Sinusoidal.Out, new Point(0.5, 1)).then(() => {
                    title.visible = false;
                });
            })
            .start();
    }

    protected hoverTitleBigWin(title: PIXI.Sprite, repeats: number = 1) {
        TweenJS.removeAll(title.scale);
        new TweenJS.Tween(title.scale)
            .to({ x: this.TITLE_ZOOM_TWEEN, y: this.TITLE_ZOOM_TWEEN }, 850)
            .yoyo(true)
            .repeat(repeats)
            .onComplete(() => {
                this.zoom(title, title.scale.x, 0, 300, TweenJS.Easing.Sinusoidal.Out, new Point(0.5, 1)).then(() => {
                    title.visible = false;
                });
            })
            .start();
    }

    protected explodeEmitter(emitter: particles.Emitter, duration: number) {
        this.resetEmitter(emitter);

        emitter.emit = true;

        Timer.setTimeout(() => {
            emitter.emit = false;
        }, duration);
    }

    protected clearTimers() {
        if (this.timers) {
            for (const timer of this.timers) {
                Timer.clearTimeout(timer);
            }
            this.timers = [];
        }
    }

    protected zoomOut(sprite: PIXI.Sprite | PIXI.extras.BitmapText, duration: number): Contract<void> {
        if (sprite instanceof PIXI.Sprite && sprite.anchor.x == 0) this.centerElement(sprite as any, 0.5, 0.5);
        return new Contract(resolve => {
            new TweenJS.Tween(sprite.scale)
                .to({ x: 0, y: 0 }, duration)
                .onComplete(resolve)
                .start();
        });
    }

    protected zoomElement(sprite: PIXI.Sprite | PIXI.extras.BitmapText) {
        return new Contract(resolve => {
            if (sprite instanceof PIXI.Sprite) this.centerElement(sprite, 0.5, 0);
            else {
                sprite.anchor = 0.5;
                sprite.x += sprite.width * sprite.anchor;
                sprite.y += sprite.height * sprite.anchor;
            }

            sprite.scale.set(0, 0);
            new TweenJS.Tween(sprite.scale)
                .to({ x: 1.15, y: 1.15 }, 350)
                .onComplete(() => {
                    this.hover(sprite, 1, 350, false);
                    resolve(null);
                })
                .start();
        });
    }

    protected emphasize(el: PIXI.Sprite | PIXI.extras.BitmapText, size: number, delay: number, wait: number, returnSize: number = 1) {
        return new Contract(resolve => {
            this.timers.push(
                Timer.setTimeout(() => {
                    TweenJS.removeAll(el.scale);
                    new TweenJS.Tween(el.scale)
                        .to({ x: size, y: size }, 200)
                        .onComplete(() => {
                            new TweenJS.Tween(el.scale)
                                .to({ x: returnSize, y: returnSize }, 200)
                                .delay(wait)
                                .onComplete(resolve)
                                .start();
                        })
                        .start();
                }, delay)
            );
        });
    }

    protected hover(target: PIXI.Sprite | PIXI.extras.BitmapText, scale: number, duration: number, setInitialScale: boolean = true): TweenJS.Tween {
        TweenJS.removeAll(target.scale);
        const tween = new TweenJS.Tween(target.scale)
            .to({ x: scale, y: scale }, duration)
            .easing(TweenJS.Easing.Quadratic.InOut)
            .yoyo(true)
            .repeat(Infinity)
            .onUpdate(() => {
                if (!target) {
                    TweenJS.remove(tween);
                    return;
                }
            });

        if (setInitialScale) target.scale.x = target.scale.y = 1;
        tween.start();

        return tween;
    }

    protected centerElement(sprite: PIXI.Sprite | Sprite, anchorX: number = 0, anchorY: number) {
        sprite.x -= sprite.width * sprite.anchor.x;
        sprite.y -= sprite.width * sprite.anchor.y;
        sprite.anchor.set(anchorX, anchorY);
        sprite.x += sprite.width * anchorX;
        sprite.y += sprite.height * anchorY;
    }

    protected resetEmitter(emitter: particles.Emitter) {
        emitter.emit = false;
        emitter.cleanup();
    }

    public getSelectedTiers(freespins: boolean = false): any[] {
        let winnings;
        if (freespins) {
            winnings = GameState.getCurrentGame().getRunningFreespinsWinTotal();
        } else if (GameState.getCurrentGame().getCurrentRecord().beenPaid || GameState.getCurrentGame().getCurrentRecord() instanceof FreespinRecord) {
            winnings = GameState.getCurrentGame().totalCreditsWon;
        } else {
            winnings = GameState.getCurrentGame().getCurrentRecord().creditsWon;
        }

        const stake = betModel.getTotalBet();

        const multiplier = winnings / stake;

        const selectedLevels: any[] = [];
        let tiers = Components.config.bigWin.tiers;
        for (const level of tiers) {
            if (level.threshold > multiplier) {
                break;
            } else {
                selectedLevels.push(level);
            }
        }

        return selectedLevels;
    }

    protected resetEverything() {
        RevolverWinTransitions.background = Layers.CelebrationBackground.getSprite('filter');
        RevolverWinTransitions.background.visible = false;

        setFullScreenCelebrationFilter();

        RevolverWinTransitions.coins = Layers.CelebrationBackground.getParticle('coins');
        RevolverWinTransitions.coins.emit = false;

        RevolverWinTransitions.winValue = Layers.CelebrationForeground.getBitmapText('value');
        RevolverWinTransitions.winText = Layers.CelebrationForeground.getSprite('win');

        RevolverWinTransitions.glowStack = [];
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite('glow_back'));
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite('glow_mid'));
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite('glow_front'));
    }
}



// WEBPACK FOOTER //
// ./src/transitions/revolver-win-transitions.ts